import { useEffect, useMemo, useState } from "react";
import { Plus, Wallet, ArrowDownLeft, ArrowUpRight, BookOpen, Trash2, Pencil, Copy, Settings2, Landmark, AlertTriangle, Link2, Zap } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { DataTable, type Column } from "../components/ui/DataTable";
import { StatCard } from "../components/ui/StatCard";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { PrintTable } from "../components/PrintTable";
import { Select } from "../components/ui/Select";
import { KoppelPanel } from "../components/kasboek/KoppelPanel";
import { eur, fmtDate } from "../lib/format";
import api from "../lib/api";

interface CashAccount {
  id: string;
  number: string;
  name: string;
  kind: "INCOME" | "EXPENSE" | "NEUTRAL";
  isSystem: boolean;
  isActive: boolean;
  _count?: { entries: number };
}

interface CashEntry {
  id: string;
  date: string;
  description: string;
  amount: number; // getekend: + = In, − = Uit
  amountExclVat: number;
  vatAmount: number;
  notes?: string | null;
  isProcessed: boolean;
  accountId: string;
  vatRateId?: string | null;
  account: CashAccount;
  vatRate?: { id: string; name: string; percentage: number } | null;
  createdBy?: { firstName?: string; lastName?: string } | null;
  // Kasboek-koppeling (uitgave contant betaald / factuur contant voldaan).
  expense?: { id: string; description: string; amountEurInclVat: number } | null;
  payment?: { id: string; amountEur: number; invoice?: { id: string; number: string } | null } | null;
}

interface QuarterSummary {
  opening: number;
  quarters: { q: number; in: number; uit: number; netto: number }[];
  totals: { in: number; uit: number; netto: number };
  closing: number;
}

const KIND_LABEL: Record<string, string> = { INCOME: "Omzet", EXPENSE: "Kosten", NEUTRAL: "Neutraal" };
const KIND_STYLE: Record<string, string> = {
  INCOME: "bg-emerald-100/80 text-emerald-700 border-emerald-200",
  EXPENSE: "bg-amber-100/80 text-amber-700 border-amber-200",
  NEUTRAL: "bg-slate-100/80 text-slate-600 border-slate-200",
};

const YEARS: number[] = (() => {
  const y = new Date().getFullYear();
  return Array.from({ length: y - 2014 }, (_, i) => y - i);
})();

export default function Kasboek() {
  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [vatRates, setVatRates] = useState<any[]>([]);
  const [summary, setSummary] = useState<QuarterSummary | null>(null);
  const [openingInfo, setOpeningInfo] = useState<{ default: number; override: { openingBalance: number } | null; applicable: number } | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [accountFilter, setAccountFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [flash, setFlash] = useState("");

  // Regel-modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CashEntry | null>(null);
  const [fDate, setFDate] = useState(new Date().toISOString().split("T")[0]);
  const [fAccountId, setFAccountId] = useState("");
  const [fDir, setFDir] = useState<"in" | "uit">("in");
  const [fAmount, setFAmount] = useState("");
  const [fDescription, setFDescription] = useState("");
  const [fVatRateId, setFVatRateId] = useState("");
  const [fNotes, setFNotes] = useState("");
  const [fProcessed, setFProcessed] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Rubrieken-modal (CRUD)
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [newAccNumber, setNewAccNumber] = useState("");
  const [newAccName, setNewAccName] = useState("");
  const [newAccKind, setNewAccKind] = useState<"INCOME" | "EXPENSE" | "NEUTRAL">("EXPENSE");
  const [accSaving, setAccSaving] = useState(false);
  const [accError, setAccError] = useState("");

  // Beginsaldo-modal
  const [openingOpen, setOpeningOpen] = useState(false);
  const [openingDefault, setOpeningDefault] = useState("");
  const [openingOverride, setOpeningOverride] = useState("");
  const [openingSaving, setOpeningSaving] = useState(false);
  const [openingError, setOpeningError] = useState("");

  // Verwijder-bevestiging
  const [deleteTarget, setDeleteTarget] = useState<CashEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Koppelen (kasboekregel ↔ uitgave/factuur)
  const [koppelTarget, setKoppelTarget] = useState<CashEntry | null>(null);
  const [autoMatching, setAutoMatching] = useState(false);

  const [tab, setTab] = useState<"dagboek" | "overzicht">("dagboek");
  const [visibleRows, setVisibleRows] = useState<CashEntry[]>([]);

  const parseAmt = (s: string): number => {
    const n = parseFloat(String(s).replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const load = async (y = year) => {
    setLoading(true);
    try {
      const [ent, acc, sum, open] = await Promise.all([
        api.get("/api/kasboek/entries", { params: { year: y } }),
        api.get("/api/kasboek/accounts"),
        api.get("/api/kasboek/summary", { params: { year: y } }),
        api.get("/api/kasboek/openings", { params: { year: y } }),
      ]);
      setEntries(ent.data.entries);
      setAccounts(acc.data.accounts);
      setSummary(sum.data);
      setOpeningInfo(open.data);
      setPageError("");
    } catch (err: any) {
      setPageError(err?.response?.data?.error || "Kasboek laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => {
    api.get("/api/vat-rates").then((res) => setVatRates(res.data)).catch(() => {});
  }, []);

  const flashMsg = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 3500);
  };

  const opening = openingInfo?.applicable ?? 0;

  // Doorlopend saldo per regel (backend levert date-asc; bereken in volgorde).
  const withSaldo = useMemo(() => {
    let running = opening;
    return entries.map((e) => {
      running += Number(e.amount);
      return { ...e, _saldo: Math.round(running * 100) / 100 };
    });
  }, [entries, opening]);

  const totIn = useMemo(() => Math.round(entries.reduce((s, e) => s + (Number(e.amount) >= 0 ? Number(e.amount) : 0), 0) * 100) / 100, [entries]);
  const totUit = useMemo(() => Math.round(entries.reduce((s, e) => s + (Number(e.amount) < 0 ? -Number(e.amount) : 0), 0) * 100) / 100, [entries]);
  const closing = Math.round((opening + totIn - totUit) * 100) / 100;

  const activeAccounts = accounts.filter((a) => a.isActive);

  // ── Regel-modal openen/sluiten ────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    setFDate(new Date().toISOString().split("T")[0]);
    setFAccountId(activeAccounts[0]?.id || accounts[0]?.id || "");
    setFDir("in");
    setFAmount("");
    setFDescription("");
    setFVatRateId("");
    setFNotes("");
    setFProcessed(true);
    setSaveError("");
    setModalOpen(true);
  };

  const openEdit = (e: CashEntry) => {
    setEditing(e);
    setFDate(new Date(e.date).toISOString().split("T")[0]);
    setFAccountId(e.accountId);
    setFDir(Number(e.amount) >= 0 ? "in" : "uit");
    setFAmount(String(Math.abs(Number(e.amount))).replace(".", ","));
    setFDescription(e.description);
    setFVatRateId(e.vatRateId || "");
    setFNotes(e.notes || "");
    setFProcessed(e.isProcessed);
    setSaveError("");
    setModalOpen(true);
  };

  /** Dupliceren: modal vullen met de waarden van de regel, maar in
   *  aanmaak-modus (editing = null) zodat het een nieuwe POST wordt en de
   *  gebruiker eerst kan bijwerken. */
  const openDuplicate = (e: CashEntry) => {
    setEditing(null);
    setFDate(new Date(e.date).toISOString().split("T")[0]);
    setFAccountId(e.accountId);
    setFDir(Number(e.amount) >= 0 ? "in" : "uit");
    setFAmount(String(Math.abs(Number(e.amount))).replace(".", ","));
    setFDescription(`${e.description} (kopie)`);
    setFVatRateId(e.vatRateId || "");
    setFNotes(e.notes || "");
    setFProcessed(e.isProcessed);
    setSaveError("");
    setModalOpen(true);
  };

  /** Rubrieksoort bepaalt of een BTW-code zinvol is (NEUTRAL = nooit). */
  const selectedKind = accounts.find((a) => a.id === fAccountId)?.kind ?? "EXPENSE";
  const vatPct = vatRates.find((v: any) => v.id === fVatRateId) ? Number(vatRates.find((v: any) => v.id === fVatRateId).percentage) : 0;
  const fAmt = parseAmt(fAmount);
  const fNetto = vatPct > 0 ? fAmt / (1 + vatPct / 100) : fAmt;
  const fBtw = fAmt - fNetto;

  const saveEntry = async () => {
    setSaveError("");
    if (!fDescription.trim()) return setSaveError("Vul een omschrijving in.");
    if (!fAccountId) return setSaveError("Kies een rubriek.");
    const amt = parseAmt(fAmount);
    if (!amt) return setSaveError("Vul een bedrag in (niet 0).");
    setSaving(true);
    try {
      const body = {
        date: fDate,
        accountId: fAccountId,
        description: fDescription.trim(),
        amount: fDir === "in" ? amt : -amt,
        vatRateId: selectedKind === "NEUTRAL" ? "" : fVatRateId,
        notes: fNotes,
        isProcessed: fProcessed,
      };
      if (editing) await api.patch(`/api/kasboek/entries/${editing.id}`, body);
      else await api.post("/api/kasboek/entries", body);
      setModalOpen(false);
      await load();
      flashMsg(editing ? "Regel bijgewerkt." : "Regel toegevoegd.");
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const toggleProcessed = async (e: CashEntry) => {
    try {
      await api.post("/api/kasboek/entries/bulk-processed", { ids: [e.id], isProcessed: !e.isProcessed });
      setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, isProcessed: !e.isProcessed } : x)));
    } catch (err: any) {
      setPageError(err?.response?.data?.error || "Verwerkt-toggle mislukt");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/kasboek/entries/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
      flashMsg("Regel verwijderd.");
    } catch (err: any) {
      setPageError(err?.response?.data?.error || "Verwijderen mislukt");
    } finally {
      setDeleting(false);
    }
  };

  // ── Koppelen (kasboekregel ↔ uitgave / factuur) ───────────────────────────
  const runAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const { data } = await api.post("/api/kasboek/auto-match");
      alert(
        `Auto-match klaar: ${data.autoMatched} regel(s) automatisch gekoppeld, ${data.suggested} voorstel(len) gevonden.\n\nBekijk de voorstellen via het koppel-icoon bij de regel.`,
      );
      await load();
    } catch (err: any) {
      setPageError(err?.response?.data?.error || "Auto-match mislukt");
    } finally {
      setAutoMatching(false);
    }
  };

  // ── Rubrieken-CRUD ────────────────────────────────────────────────────────
  const saveAccount = async () => {
    setAccError("");
    if (!newAccNumber.trim() || !newAccName.trim()) return setAccError("Vul nummer en naam in.");
    setAccSaving(true);
    try {
      await api.post("/api/kasboek/accounts", { number: newAccNumber.trim(), name: newAccName.trim(), kind: newAccKind });
      setNewAccNumber("");
      setNewAccName("");
      const acc = await api.get("/api/kasboek/accounts");
      setAccounts(acc.data.accounts);
      flashMsg("Rubriek toegevoegd.");
    } catch (err: any) {
      setAccError(err?.response?.data?.error || "Rubriek opslaan mislukt");
    } finally {
      setAccSaving(false);
    }
  };

  const toggleAccount = async (a: CashAccount) => {
    try {
      await api.patch(`/api/kasboek/accounts/${a.id}`, { isActive: !a.isActive });
      const acc = await api.get("/api/kasboek/accounts");
      setAccounts(acc.data.accounts);
    } catch (err: any) {
      setAccError(err?.response?.data?.error || "Wijzigen mislukt");
    }
  };

  const deleteAccount = async (a: CashAccount) => {
    setAccError("");
    try {
      await api.delete(`/api/kasboek/accounts/${a.id}`);
      const acc = await api.get("/api/kasboek/accounts");
      setAccounts(acc.data.accounts);
      flashMsg("Rubriek verwijderd.");
    } catch (err: any) {
      setAccError(err?.response?.data?.error || "Verwijderen mislukt");
    }
  };

  // ── Beginsaldi ────────────────────────────────────────────────────────────
  const openOpening = () => {
    setOpeningDefault(openingInfo ? String(openingInfo.default).replace(".", ",") : "0");
    setOpeningOverride(openingInfo?.override ? String(openingInfo.override.openingBalance).replace(".", ",") : "");
    setOpeningError("");
    setOpeningOpen(true);
  };

  const saveOpening = async () => {
    setOpeningError("");
    setOpeningSaving(true);
    try {
      const d = parseAmt(openingDefault);
      await api.put("/api/kasboek/openings/default", { openingBalance: d });
      const o = parseAmt(openingOverride);
      if (openingOverride.trim()) await api.put(`/api/kasboek/openings/${year}`, { openingBalance: o });
      else await api.delete(`/api/kasboek/openings/${year}`);
      setOpeningOpen(false);
      await load();
      flashMsg("Beginsaldo opgeslagen.");
    } catch (err: any) {
      setOpeningError(err?.response?.data?.error || "Beginsaldo opslaan mislukt");
    } finally {
      setOpeningSaving(false);
    }
  };

  const removeOverride = async () => {
    setOpeningSaving(true);
    try {
      await api.delete(`/api/kasboek/openings/${year}`);
      setOpeningOverride("");
      await load();
    } catch (err: any) {
      setOpeningError(err?.response?.data?.error || "Override verwijderen mislukt");
    } finally {
      setOpeningSaving(false);
    }
  };

  // ── Kolommen ──────────────────────────────────────────────────────────────
  const columns: Column<any>[] = [
    { key: "date", header: "Datum", accessor: (e) => fmtDate(e.date), sortValue: (e) => new Date(e.date).getTime(), width: "7rem" },
    {
      key: "account",
      header: "Rubriek",
      accessor: (e) => `${e.account.number} ${e.account.name}`,
      render: (e) => (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${KIND_STYLE[e.account.kind]}`}>
            {e.account.number}
          </span>
          <span className="text-slate-700">{e.account.name}</span>
        </div>
      ),
    },
    { key: "description", header: "Omschrijving", accessor: (e) => e.description },
    {
      key: "in",
      header: "In",
      accessor: (e) => (Number(e.amount) >= 0 ? Number(e.amount) : 0),
      sortValue: (e) => (Number(e.amount) >= 0 ? Number(e.amount) : 0),
      render: (e) => (Number(e.amount) >= 0 ? <span className="text-emerald-600 font-semibold">{eur(Number(e.amount))}</span> : <span className="text-slate-300">—</span>),
      className: "text-right",
    },
    {
      key: "uit",
      header: "Uit",
      accessor: (e) => (Number(e.amount) < 0 ? -Number(e.amount) : 0),
      sortValue: (e) => (Number(e.amount) < 0 ? -Number(e.amount) : 0),
      render: (e) => (Number(e.amount) < 0 ? <span className="text-rose-600 font-semibold">{eur(-Number(e.amount))}</span> : <span className="text-slate-300">—</span>),
      className: "text-right",
    },
    {
      key: "vat",
      header: "BTW",
      accessor: (e) => (e.vatRate ? `${Number(e.vatRate.percentage)}%` : "—"),
      render: (e) =>
        e.vatRate && Number(e.vatRate.percentage) > 0 ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100/80 text-sky-700 border border-sky-200 text-xs font-semibold">{Number(e.vatRate.percentage)}%</span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "koppeling",
      header: "Koppeling",
      accessor: (e) => (e.expense ? e.expense.description : e.payment?.invoice?.number || ""),
      sortable: false,
      render: (e) =>
        e.expense ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100/80 text-emerald-700 border border-emerald-200 text-xs font-semibold" title={`Uitgave betaald via kas: ${e.expense.description}`}>
            <Link2 size={12} /> Uitgave
          </span>
        ) : e.payment ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100/80 text-sky-700 border border-sky-200 text-xs font-semibold" title={`Factuur voldaan via kas: ${e.payment.invoice?.number ?? ""}`}>
            <Link2 size={12} /> Factuur {e.payment.invoice?.number ?? ""}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "saldo",
      header: "Saldo",
      accessor: (e) => e._saldo,
      sortable: false,
      render: (e) => <span className="font-semibold text-slate-800 tabular-nums">{eur(e._saldo)}</span>,
      className: "text-right",
    },
    {
      key: "isProcessed",
      header: "Verwerkt",
      accessor: (e) => (e.isProcessed ? "Ja" : "Nee"),
      sortable: false,
      render: (e) => (
        <input
          type="checkbox"
          checked={e.isProcessed}
          onChange={() => toggleProcessed(e)}
          className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          title="Verwerkt-vinkje (workflow)"
        />
      ),
      className: "text-center",
      width: "6rem",
    },
    {
      key: "actions",
      header: "",
      accessor: () => "",
      sortable: false,
      render: (e) => (
        <div className="flex items-center justify-end gap-1">
          <button type="button" onClick={() => setKoppelTarget(e)} title="Koppelen (uitgave / factuur)" className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-600">
            <Link2 size={16} />
          </button>
          <button type="button" onClick={() => openEdit(e)} title="Bewerken" className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <Pencil size={16} />
          </button>
          <button type="button" onClick={() => openDuplicate(e)} title="Dupliceren" className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <Copy size={16} />
          </button>
          <button type="button" onClick={() => setDeleteTarget(e)} title="Verwijderen" className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const accountOptions = [
    { value: "", label: "Alle rubrieken" },
    ...accounts.map((a) => ({ value: a.id, label: `${a.number} — ${a.name}${a.isActive ? "" : " (inactief)"}` })),
  ];

  const filteredEntries = accountFilter ? withSaldo.filter((e) => e.accountId === accountFilter) : withSaldo;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Kasboek"
        subtitle="Kas-omzet, extra uitgaven en ritvergoedingen noteren — met doorlopend kassaldo"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))} options={YEARS.map((y) => ({ value: String(y), label: String(y) }))} className="w-24" />
            {tab === "dagboek" && (
              <Button variant="outline" onClick={runAutoMatch} loading={autoMatching}>
                <Zap size={16} /> Auto-match
              </Button>
            )}
            <Button variant="outline" onClick={() => setAccountsOpen(true)}>
              <Settings2 size={16} /> Rubrieken
            </Button>
            <Button variant="outline" onClick={openOpening}>
              <Landmark size={16} /> Beginsaldo
            </Button>
            <Button onClick={openNew}>
              <Plus size={18} /> Nieuwe regel
            </Button>
          </div>
        }
      />

      {pageError && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <AlertTriangle size={18} /> {pageError}
        </div>
      )}
      {flash && (
        <div className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">{flash}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={`Beginsaldo ${year}`} value={eur(opening)} icon={Landmark} color="indigo" />
        <StatCard label="Totaal in" value={eur(totIn)} icon={ArrowDownLeft} color="emerald" delay={0.05} />
        <StatCard label="Totaal uit" value={eur(totUit)} icon={ArrowUpRight} color="rose" delay={0.1} />
        <StatCard label="Eindsaldo" value={eur(closing)} icon={Wallet} color={closing < 0 ? "rose" : "brand"} delay={0.15} />
      </div>

      {/* Dubbel-tel-hint */}
      <p className="text-xs text-slate-500">
        Tip: boek kas-omzet alleen voor contante verkoop die níet op een factuur staat. Contante betaling van een bestaande
        factuur of uitgave koppel je via het <strong>koppel-icoon</strong> — de regel telt dan automatisch als kruispost en de
        factuur/uitgave geldt als betaald (geen dubbele bedragen in BTW en balans).
      </p>

      {/* Tabs */}
      <div className="flex gap-2">
        {(
          [
            { key: "dagboek", label: "Dagboek" },
            { key: "overzicht", label: "Overzicht per kwartaal" },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-brand-500 text-white shadow-lg shadow-brand-500/25" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dagboek" ? (
        <>
        <DataTable
          rows={filteredEntries}
          columns={columns}
          rowKey={(e) => e.id}
          searchPlaceholder="Zoek regels..."
          storageKey="kasboek"
          initialSort={{ key: "date", dir: "asc" }}
          emptyMessage={loading ? "Laden..." : "Nog geen kasboek-regels dit jaar. Klik op 'Nieuwe regel'."}
          onVisibleRowsChange={setVisibleRows}
          toolbar={
            <>
              <Select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} options={accountOptions} className="w-72" />
              <TableExportToolbar
                rows={visibleRows}
                columns={columns as Column<any>[]}
                filename={`kasboek-${year}`}
                source="Kasboek"
                printTitle="Kasboek"
              />
            </>
          }
        />
        </>
      ) : (
        <div className="surface-card p-6 overflow-x-auto">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <BookOpen size={20} /> {year} per kwartaal
          </h3>
          {summary ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 font-semibold">Kwartaal</th>
                  {summary.quarters.map((q) => (
                    <th key={q.q} className="py-2 text-right font-semibold">Kwrt {q.q}</th>
                  ))}
                  <th className="py-2 text-right font-semibold">Totaal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 font-semibold text-slate-700">In</td>
                  {summary.quarters.map((q) => (
                    <td key={q.q} className="py-2 text-right text-emerald-600 tabular-nums">{eur(q.in)}</td>
                  ))}
                  <td className="py-2 text-right font-semibold text-emerald-700 tabular-nums">{eur(summary.totals.in)}</td>
                </tr>
                <tr>
                  <td className="py-2 font-semibold text-slate-700">Uit</td>
                  {summary.quarters.map((q) => (
                    <td key={q.q} className="py-2 text-right text-rose-600 tabular-nums">{eur(q.uit)}</td>
                  ))}
                  <td className="py-2 text-right font-semibold text-rose-700 tabular-nums">{eur(summary.totals.uit)}</td>
                </tr>
                <tr>
                  <td className="py-2 font-semibold text-slate-700">Netto</td>
                  {summary.quarters.map((q) => (
                    <td key={q.q} className={`py-2 text-right tabular-nums ${q.netto < 0 ? "text-rose-600" : "text-slate-700"}`}>{eur(q.netto)}</td>
                  ))}
                  <td className={`py-2 text-right font-bold tabular-nums ${summary.totals.netto < 0 ? "text-rose-700" : "text-slate-800"}`}>{eur(summary.totals.netto)}</td>
                </tr>
              </tbody>
              <tfoot className="border-t-2 border-slate-200">
                <tr>
                  <td className="py-3 text-sm text-slate-500">
                    Beginsaldo: <strong className="text-slate-700">{eur(summary.opening)}</strong>
                  </td>
                  <td colSpan={summary.quarters.length} />
                  <td className="py-3 text-right">
                    <span className="text-sm text-slate-500 mr-2">Eindsaldo:</span>
                    <strong className={`text-base ${summary.closing < 0 ? "text-rose-700" : "text-slate-900"}`}>{eur(summary.closing)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="text-sm text-slate-400 py-8 text-center">Laden...</div>
          )}
        </div>
      )}

      <PrintTable rows={visibleRows} columns={columns as Column<any>[]} title={`Kasboek ${year}`} />

      {/* Regel-modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Regel bewerken" : "Nieuwe kasboek-regel"}
        subtitle={editing ? "Pas de gegevens aan" : "Kas-omzet, kas-uitgave of ritvergoeding noteren"}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={saving}>
              Annuleren
            </Button>
            <Button onClick={saveEntry} loading={saving}>
              Opslaan
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-1">Rubriek</label>
            <Select
              value={fAccountId}
              onChange={(e) => setFAccountId(e.target.value)}
              options={accounts
                .filter((a) => a.isActive || a.id === fAccountId)
                .map((a) => ({ value: a.id, label: `${a.number} — ${a.name} (${KIND_LABEL[a.kind]})` }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1">Datum</label>
              <Input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1">Richting</label>
              <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                {(
                  [
                    { key: "in", label: "In (kas +)", cls: fDir === "in" ? "bg-emerald-500 text-white" : "bg-white text-slate-600 hover:bg-emerald-50" },
                    { key: "uit", label: "Uit (kas −)", cls: fDir === "uit" ? "bg-rose-500 text-white" : "bg-white text-slate-600 hover:bg-rose-50" },
                  ] as const
                ).map((d) => (
                  <button key={d.key} type="button" onClick={() => setFDir(d.key)} className={`flex-1 py-2 text-sm font-semibold transition-colors ${d.cls}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-1">Omschrijving</label>
            <Input value={fDescription} onChange={(e) => setFDescription(e.target.value)} placeholder="Bijv. kassa-dagomzet 12 aug / uitrit klant X" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1">Bedrag (incl. BTW)</label>
              <Input value={fAmount} onChange={(e) => setFAmount(e.target.value)} placeholder="0,00" inputMode="decimal" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1">BTW-code</label>
              {selectedKind === "NEUTRAL" ? (
                <Input value="Geen BTW" disabled title="Neutrale rubrieken tellen niet mee in het BTW-rapport" />
              ) : (
                <Select value={fVatRateId} onChange={(e) => setFVatRateId(e.target.value)} options={[{ value: "", label: "Geen BTW (0%)" }, ...vatRates.map((v: any) => ({ value: v.id, label: `${Number(v.percentage)}%${v.name ? ` — ${v.name}` : ""}` }))]} />
              )}
            </div>
          </div>
          {fAmt > 0 && fVatRateId && selectedKind !== "NEUTRAL" && (
            <p className="text-xs text-slate-500">
              Netto {eur(fNetto)} + BTW {eur(fBtw)} ({vatPct}%)
            </p>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-1">Notities (optioneel)</label>
            <Input value={fNotes} onChange={(e) => setFNotes(e.target.value)} placeholder="Bijv. aantal km bij ritvergoeding" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={fProcessed} onChange={(e) => setFProcessed(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            Verwerkt
          </label>
          {saveError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <AlertTriangle size={16} /> {saveError}
            </div>
          )}
        </div>
      </Modal>

      {/* Rubrieken-modal */}
      <Modal isOpen={accountsOpen} onClose={() => { setAccountsOpen(false); setAccError(""); }} title="Rubrieken beheren" subtitle="Grootboekrubrieken van het kasboek" size="md">
        <div className="space-y-4">
          {accError && (
            <div className="flex items-center gap-2 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
              <AlertTriangle size={16} /> {accError}
            </div>
          )}
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono text-xs text-slate-500 w-14 shrink-0">{a.number}</span>
                <span className={`flex-1 ${a.isActive ? "text-slate-800" : "text-slate-400 line-through"}`}>{a.name}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${KIND_STYLE[a.kind]}`}>{KIND_LABEL[a.kind]}</span>
                <button type="button" onClick={() => toggleAccount(a)} title={a.isActive ? "Deactiveren" : "Activeren"} className={`px-2 py-1 rounded-lg text-xs font-semibold ${a.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {a.isActive ? "Actief" : "Inactief"}
                </button>
                <button
                  type="button"
                  onClick={() => deleteAccount(a)}
                  disabled={a.isSystem || (a._count?.entries ?? 0) > 0}
                  title={a.isSystem ? "Systeemrubriek kan niet verwijderd worden" : (a._count?.entries ?? 0) > 0 ? "Rubriek is in gebruik" : "Verwijderen"}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[5.5rem_1fr_auto] gap-2 items-end">
            <Input value={newAccNumber} onChange={(e) => setNewAccNumber(e.target.value)} placeholder="Nr." />
            <Input value={newAccName} onChange={(e) => setNewAccName(e.target.value)} placeholder="Naam nieuwe rubriek" />
            <Select value={newAccKind} onChange={(e) => setNewAccKind(e.target.value as any)} options={[{ value: "INCOME", label: "Omzet" }, { value: "EXPENSE", label: "Kosten" }, { value: "NEUTRAL", label: "Neutraal" }]} className="w-28" />
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={saveAccount} loading={accSaving}>
              <Plus size={15} /> Rubriek toevoegen
            </Button>
          </div>
        </div>
      </Modal>

      {/* Beginsaldo-modal */}
      <Modal
        isOpen={openingOpen}
        onClose={() => setOpeningOpen(false)}
        title="Beginsaldo kas"
        subtitle={`Startsaldo voor ${year} (default + per-jaar override)`}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setOpeningOpen(false)} disabled={openingSaving}>
              Annuleren
            </Button>
            <Button onClick={saveOpening} loading={openingSaving}>
              Opslaan
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {openingError && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{openingError}</div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-1">Standaard beginsaldo (alle jaren)</label>
            <Input value={openingDefault} onChange={(e) => setOpeningDefault(e.target.value)} inputMode="decimal" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-1">Override voor {year} (optioneel)</label>
            <Input value={openingOverride} onChange={(e) => setOpeningOverride(e.target.value)} placeholder="leeg = standaard gebruiken" inputMode="decimal" />
            <p className="text-xs text-slate-400 mt-1">Laat leeg om de standaard beginsaldo te gebruiken voor {year}.</p>
          </div>
          {openingInfo?.override && (
            <Button variant="outline" size="sm" onClick={removeOverride} disabled={openingSaving}>
              Override voor {year} verwijderen
            </Button>
          )}
        </div>
      </Modal>

      {/* Koppel-modal (kasboekregel ↔ uitgave / factuur) */}
      <KoppelPanel
        isOpen={!!koppelTarget}
        onClose={() => setKoppelTarget(null)}
        entry={koppelTarget}
        onDone={async () => {
          await load();
          flashMsg("Koppeling bijgewerkt.");
        }}
      />

      {/* Verwijder-bevestiging */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Regel verwijderen"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annuleren
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting}>
              Verwijderen
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          Weet je zeker dat je de regel <strong>{deleteTarget?.description}</strong> ({eur(Math.abs(Number(deleteTarget?.amount ?? 0)))}) wilt verwijderen?
          Het doorlopend saldo en de rapporten worden bijgewerkt.
        </p>
      </Modal>
    </div>
  );
}